import { requiresClaimProjectionEngine } from '../../FundingPolicyTypeDef';
import type { ClientDerivativeOptions } from '../calcClientDerivatives';

// Single resolution point for the claim-projection engine.
//
// Defaults to TRUE: the engine is how every client is priced. This gate exists
// only as an emergency brake — the `claimProjectionEngineEnabled` app-config flag is
// resolved when the client is loaded and carried on the client itself, so it can
// be turned off without a deploy.
//
// Read by BOTH the funding calc (which engine prices claims) and the Stage-1
// phase-cost calc (which cost model prices care). Those must agree within a
// single derivative pass: pricing care on one model and claims on the other
// produces a coverage percentage whose numerator and denominator come from
// different worlds. Resolving from the client — rather than threading a flag
// through ~30 call sites — is what makes disagreeing impossible.
//
// An explicit option still wins, so preview and comparison flows can force a
// model without touching the client.
export function resolveUseClaimProjectionEngine(
  client: Client,
  options: ClientDerivativeOptions = {},
): boolean {
  const resolved = options.useClaimProjectionEngine ?? client.projectionEngineEnabled ?? true;
  if (resolved) return true;

  // A client holding a product that MUST be engine-priced cannot be braked.
  // `runPolicyCalculations` keeps pricing those policies on the engine
  // regardless (legacy would report a materially wrong number for them), so
  // braking the rest of the client would leave that policy's engine coverage
  // divided by a legacy cost basis — a percentage whose numerator and
  // denominator come from different models.
  //
  // Refusing to brake the whole client is the only outcome that keeps one model
  // per pass. It means the brake is a no-op for these clients, which is the
  // honest result: there is no safe legacy answer to fall back to.
  return clientHoldsMandatoryEnginePolicy(client);
}

function clientHoldsMandatoryEnginePolicy(client: Client): boolean {
  const policies = client.multipleFundingSources?.ltcPolicy ?? {};
  return Object.values(policies).some(
    policy => !policy.isDeactivated && requiresClaimProjectionEngine(policy.policyType),
  );
}
